import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMerchantRuleInput,
  MerchantRule,
  MerchantRulesListResponse,
  RenameMerchantInput,
  UpdateMerchantRuleInput,
} from "@money/shared";
import { apiFetch } from "./client";

export const merchantRulesKey = ["merchant-rules"] as const;

export function useMerchantRules() {
  return useQuery({
    queryKey: merchantRulesKey,
    queryFn: () => apiFetch<MerchantRulesListResponse>("/merchant-rules"),
    staleTime: 60_000,
  });
}

export function useCreateMerchantRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMerchantRuleInput) =>
      apiFetch<MerchantRule>("/merchant-rules", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: merchantRulesKey }),
  });
}

export function useUpdateMerchantRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: UpdateMerchantRuleInput }) =>
      apiFetch<MerchantRule>(`/merchant-rules/${input.id}`, {
        method: "PATCH",
        body: input.patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: merchantRulesKey }),
  });
}

export function useDeleteMerchantRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/merchant-rules/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: merchantRulesKey }),
  });
}

export function useRenameMerchant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RenameMerchantInput) =>
      apiFetch<MerchantRule | { ok: true }>("/merchant-rules/rename", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      // Affects both the rules list and every transaction's resolved name.
      qc.invalidateQueries({ queryKey: merchantRulesKey });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}
